<template>
	<div class="products-analytics-container">

		<SummaryCardBest
			ref="initial-card"
			:cards-info="todayCardsInfo"
			:data="bestProductTodayOrders.productsSeries"
			data-field-value="value" />

		<FiltersRow ref="filters" @changed-timeframe="changedTimeframe" @switched-chart="tables => showCharts = !tables" with-table-switch :loading="isLoading">
			<template #extra-filters>
				<div class="extra-filters">
					<AppButtonIcon v-if="ticketModeActive" icon-name="icon-user-white" prevent-change :active="matrixQromoActive" @click-icon="matrixQromoActive = true" />
					<AppButtonIcon icon-name="icon-filters" prevent-change :active="productFiltersActive" @click-icon="toggleOptionsMenu" />
				</div>
			</template>
		</FiltersRow>

		<ProductsChartsAggregated :dates="periodDates" v-if="showCharts" />
		<ProductsTablesAggregated :dates="periodDates" v-else />

		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="activeOptionsMenu" @toggling-popup="togglePopup">
			<ModalHeader
				:title="getString('product_filter_title')"
				:button-text="getString('product_filter_save_btn')"
				@click-button="saveSelectedMenus"/>
			<div class="label-container">
				<label class="padding-bottom-label">{{getString('product_filter_card_title')}}</label>
			</div>
			<div class="menus-container">
				<MultiMenuFilter ref="menu-filter"/>
			</div>
		</AppBottomSheet>

		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="matrixQromoActive" @toggling-popup="toggleBottomSheetQromoMatrix">
			<ProductsMatrix v-if="matrixQromoActive" :dates="periodDates" />
		</AppBottomSheet>
	</div>
</template>

<script>
import {useAnalyticsStore, useMenuStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import SummaryCardBest from "@/console-new/components/analytics/SummaryCardBest.vue";
import FiltersRow from "@/console-new/components/analytics/FiltersRow.vue";
import ProductsChartsAggregated from "@/console-new/components/analytics/ProductsChartsAggregated.vue";
import ProductsTablesAggregated from "@/console-new/components/analytics/ProductsTablesAggregated.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import MultiMenuFilter from "@/console-new/components/analytics/MultiMenuFilter.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppCard from "@/shared/components/AppCard.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import ProductsMatrix from "@/console-new/components/analytics/ProductsMatrix.vue";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const notificationStore = useNotificationStore();
		return {analyticsStore, userStore, getString: languageStore.getString, menuStore, showNotification: notificationStore.showNotification};
	},
	name: "ProductsContainer",
	components: {ProductsMatrix, AppButtonIcon, ModalHeader, MultiMenuFilter, AppBottomSheet, SummaryCardBest, FiltersRow, ProductsChartsAggregated, ProductsTablesAggregated, AppCard },
	mixins: [WindowWidth],
	data() {
		return {
			realtimeTimeout: null,
			periodDates: {
				end: this.analyticsStore.getTargetEndDate(),
				start: this.analyticsStore.getTargetStartDate(0),
				previousStart: this.analyticsStore.getTargetPreviousStartDate(0),
				previousEnd: this.analyticsStore.getTargetPreviousEndDate(0),
				timeframeIndex: 0,
			},
			showCharts: true,
			activeOptionsMenu: false,
			tmpActiveMenus: null,
			matrixQromoActive: false,
			isLoading: false,
		};
	},
	computed: {
		ticketModeActive() {
			return this.userStore.currBusiness && this.userStore.currBusiness.customer_completable_order === 1;
		},
        filteredTodayOrders() {
            const now = new Date();
            const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
            
            // Check if we have aggregated data
            if (this.analyticsStore.productsAggregatedToday && this.analyticsStore.productsAggregatedToday.products) {
                const nowRounded = new Date(now);
                if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
                    nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
                }
                nowRounded.setMinutes(0, 0, 0);
                
                const filteredData = this.analyticsStore.productsAggregatedToday.products.filter(row => {
                    const date = new Date(row.hour_bucket || row.day_bucket);
                    // hour_bucket represents the END of the hour, so 03:00 contains orders from 02:00-03:00
                    // We want to exclude the shift hour itself to avoid counting pre-shift orders
                    return date > todayShiftStart && date <= nowRounded;
                });
                
                // Create a lookup for product names
                const productMetadataLookup = {};
                if (this.analyticsStore.productsAggregatedToday.products_metadata) {
                    this.analyticsStore.productsAggregatedToday.products_metadata.forEach(product => {
                        productMetadataLookup[product.menu_item_id] = product;
                    });
                }
                
                // Transform aggregated data to match the old format for compatibility
                return filteredData.map(row => {
                    const metadata = productMetadataLookup[row.item_id] || {};
                    return {
                        date: new Date(row.hour_bucket || row.day_bucket),
                        item_id: row.item_id,
                        totalFormatted: row.gross_sales / 100,
                        quantity: row.units_sold || 0,
                        name: metadata.name || '-',
                        color: metadata.color
                    };
                });
            }
            
            // No aggregated data available
			return [];
        },
        filteredTodayPreviousOrders() {
            const now = new Date();
            const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
            const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
            
            // Check if we have aggregated data
            if (this.analyticsStore.productsAggregatedToday && this.analyticsStore.productsAggregatedToday.products) {
                // Calculate time elapsed since today's shift start
                const nowRounded = new Date(now);
                if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
                    nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
                }
                nowRounded.setMinutes(0, 0, 0);

                const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
                const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

                const filteredData = this.analyticsStore.productsAggregatedToday.products.filter(row => {
                    const date = new Date(row.hour_bucket || row.day_bucket);
                    // hour_bucket represents the END of the hour, so exclude the shift hour itself
                    return date > yesterdayShiftStart && date <= yesterdayEndTime;
                });
                
                // Create a lookup for product names
                const productMetadataLookup = {};
                if (this.analyticsStore.productsAggregatedToday.products_metadata) {
                    this.analyticsStore.productsAggregatedToday.products_metadata.forEach(product => {
                        productMetadataLookup[product.menu_item_id] = product;
                    });
                }
                
                // Transform aggregated data to match the old format for compatibility
                return filteredData.map(row => {
                    const metadata = productMetadataLookup[row.item_id] || {};
                    return {
                        date: new Date(row.hour_bucket || row.day_bucket),
                        item_id: row.item_id,
                        totalFormatted: row.gross_sales / 100,
                        quantity: row.units_sold || 0,
                        name: metadata.name || '-',
                        color: metadata.color
                    };
                });
            }
            
            // No aggregated data available
			return [];
        },
		todayCardsInfo() {
			return [
				{
					title: this.bestProductTodayOrders.bestProductName ? this.getString("best_product_param", [this.bestProductTodayOrders.bestProductName]) : this.getString("best_product"),
					value: this.bestProductTodayOrders.bestProductRevenues,
					previousValue: this.bestProductTodayOrders.bestProductRevenuesPrevious,
				},
				{
					title: this.bestProductTodayOrders.bestProductName ? this.getString("best_product_num_param", [this.bestProductTodayOrders.bestProductName]) : this.getString("best_product_num"),
					value: this.bestProductTodayOrders.bestProductOrders,
					previousValue: this.bestProductTodayOrders.bestProductOrdersPrevious,
					noCurrency: true,
				},
				{
					title: this.bestCategoryTodayOrders.bestCategoryName ? this.getString("best_product_param", [this.bestCategoryTodayOrders.bestCategoryName]) : this.getString("best_category"),
					value: this.bestCategoryTodayOrders.bestCategoryRevenues,
					previousValue: this.bestCategoryTodayOrders.bestCategoryRevenuesPrevious,
				},
				{
					title: this.bestCategoryTodayOrders.bestCategoryName ? this.getString("best_product_num_param", [this.bestCategoryTodayOrders.bestCategoryName]) : this.getString("best_category_num"),
					value: this.bestCategoryTodayOrders.bestCategoryOrders,
					previousValue: this.bestCategoryTodayOrders.bestCategoryOrdersPrevious,
					noCurrency: true,
				},
			];
		},
        bestProductTodayOrders() {
            let bestProductRevenuesPrevious = null;
            let bestProductOrdersPrevious = null;

            let products = {};

            let activeCharts = {
                products: false,
            };

            // Process aggregated data only
            this.filteredTodayOrders.forEach(item => {
                if(products[item.item_id]) {
                    products[item.item_id].revenues += item.totalFormatted;
                    products[item.item_id].orders += item.quantity;
                    products[item.item_id].data.push({date: item.date, value: item.totalFormatted});
                } else {
                    products[item.item_id] = {
                        revenues: item.totalFormatted,
                        orders: item.quantity,
                        name: item.name,
                        color: item.color,
                        item_id: item.item_id,
                        data: [{date: item.date, value: item.totalFormatted}],
                    };
                }
                activeCharts.products = true;
            });

            let bestProduct = null;
            if(activeCharts.products) {
                products = Object.values(products);
                products.sort((a, b) => b.revenues - a.revenues);
                products = products.slice(0, 6);
                for(let i = 0; i < products.length; i++) {
                    products[i].color = products[i].color ? products[i].color : this.analyticsStore.bestColors[i];
                }
                bestProduct = products[0];
            }

            if(bestProduct) {
                // Process previous period data from aggregated data only
                this.filteredTodayPreviousOrders.forEach(item => {
                    if(item.item_id === bestProduct.item_id) {
                        if (bestProductRevenuesPrevious === null) {
                            bestProductRevenuesPrevious = item.totalFormatted;
                        } else {
                            bestProductRevenuesPrevious += item.totalFormatted;
                        }
                        if (bestProductOrdersPrevious === null) {
                            bestProductOrdersPrevious = item.quantity;
                        } else {
                            bestProductOrdersPrevious += item.quantity;
                        }
                    }
                });
            }

            return {
                productsSeries: activeCharts.products ? products : [],
                bestProductName: bestProduct ? bestProduct.name : null,
                bestProductRevenues: bestProduct ? bestProduct.revenues : null,
                bestProductRevenuesPrevious: bestProductRevenuesPrevious,
                bestProductOrders: bestProduct ? bestProduct.orders : null,
                bestProductOrdersPrevious: bestProductOrdersPrevious,
            };
        },
		bestCategoryTodayOrders() {
			let bestCategoryRevenuesPrevious = null;
			let bestCategoryOrdersPrevious = null;

			let categories = {};
			let activeCharts = {
				categories: false,
			};

			// Check if we have aggregated data for categories
			if (this.analyticsStore.productsAggregatedToday && this.analyticsStore.productsAggregatedToday.categories) {
				const now = new Date();
				const aDayAgo = new Date();
				aDayAgo.setDate(aDayAgo.getDate() - 1);
				
				const nowRounded = new Date(now);
				if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
					nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
				}
				nowRounded.setMinutes(0, 0, 0);
				
				// Filter today's category data
				const filteredData = this.analyticsStore.productsAggregatedToday.categories.filter(row => {
					const date = new Date(row.hour_bucket || row.day_bucket);
					return date > aDayAgo && date <= nowRounded;
				});
				
				// Create a lookup for category names
				const categoryMetadataLookup = {};
				if (this.analyticsStore.productsAggregatedToday.categories_metadata) {
					this.analyticsStore.productsAggregatedToday.categories_metadata.forEach(category => {
						categoryMetadataLookup[category.menu_category_id] = category;
					});
				}
				
				// Process aggregated data
				filteredData.forEach(row => {
					const metadata = categoryMetadataLookup[row.category_id] || {};
					if(categories[row.category_id]) {
						categories[row.category_id].revenues += row.gross_sales / 100;
						categories[row.category_id].orders += row.units_sold || 0;
					} else {
						categories[row.category_id] = {
							revenues: row.gross_sales / 100,
							orders: row.units_sold || 0,
							name: metadata.name || '-',
							category_id: row.category_id,
						};
					}
					activeCharts.categories = true;
				});
			}

			let bestCategory = null;
			if(activeCharts.categories) {
				categories = Object.values(categories);
				categories.sort((a, b) => b.revenues - a.revenues);
				bestCategory = categories[0];
			}

			if(bestCategory && this.analyticsStore.productsAggregatedToday && this.analyticsStore.productsAggregatedToday.categories) {
				// Get previous period data
				const aDayAgo = new Date();
				aDayAgo.setDate(aDayAgo.getDate() - 1);
				const twoDaysAgo = new Date();
				twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
				
				const previousData = this.analyticsStore.productsAggregatedToday.categories.filter(row => {
					const date = new Date(row.hour_bucket || row.day_bucket);
					return date > twoDaysAgo && date <= aDayAgo;
				});
				
				previousData.forEach(row => {
					if(row.category_id === bestCategory.category_id) {
						if (bestCategoryRevenuesPrevious === null) {
							bestCategoryRevenuesPrevious = row.gross_sales / 100;
						} else {
							bestCategoryRevenuesPrevious += row.gross_sales / 100;
						}
						if (bestCategoryOrdersPrevious === null) {
							bestCategoryOrdersPrevious = row.units_sold || 0;
						} else {
							bestCategoryOrdersPrevious += row.units_sold || 0;
						}
					}
				});
			}

			return {
				bestCategoryName: bestCategory ? bestCategory.name : null,
				bestCategoryRevenues: bestCategory ? bestCategory.revenues : null,
				bestCategoryRevenuesPrevious: bestCategoryRevenuesPrevious,
				bestCategoryOrders: bestCategory ? bestCategory.orders : null,
				bestCategoryOrdersPrevious: bestCategoryOrdersPrevious,
			};
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		productFiltersActive() {
			return this.analyticsStore.loadedProducts && this.analyticsStore.activeMenus && this.analyticsStore.activeMenus.length < Object.keys(this.menuStore.menus).length;
		}
	},
	methods: {
		toggleBottomSheetQromoMatrix(isOpen) {
			if(!isOpen) {
				this.matrixQromoActive = false;
			}
		},
		updateTodayProducts(callback = null) {
			let now = new Date();
			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Fetch from yesterday's day shift to cover both today and yesterday's same period
			let startDate = this.analyticsStore.getPreviousDayShiftDatetime();
			let endDateDB = formatDateTimeWithSeconds(endDate);
			let startDateDB = formatDateTimeWithSeconds(startDate);

			// Only fetch aggregated data for today's summary cards
			this.analyticsStore.getStatsProductsAggregated(startDateDB, endDateDB, callback, true);

		},
		updateProducts() {
			this.isLoading = true;

			let startDate = this.periodDates.previousStart;
			let endDate = this.periodDates.end;

			if(startDate) {
				let endDateDB = formatDateTimeWithSeconds(endDate);
				let otherStartDateDB = formatDateTimeWithSeconds(startDate);

				// For aggregated, use the same date range as orders (previousStart to end)
				// This ensures we get data for both current and previous periods in one call
				this.analyticsStore.getStatsProductsAggregated(otherStartDateDB, endDateDB, () => {
					this.isLoading = false;
				});

			} else {
				this.isLoading = false;
			}
		},
		updateAll() {
			this.isLoading = true;

			this.updateProducts();
			this.updateTodayProducts();

		},
		changedTimeframe(start, end, previousStart, previousEnd, timeframeIndex) {
			this.periodDates.start = start;
			this.periodDates.end = end;
			this.periodDates.previousStart = previousStart;
			this.periodDates.previousEnd = previousEnd;
			this.periodDates.timeframeIndex = timeframeIndex;
			this.updateProducts();
		},
		togglePopup(newState) {
			if(!newState) {
				this.activeOptionsMenu = false;
			}
		},
		toggleOptionsMenu() {
			this.activeOptionsMenu = true;
		},
		saveSelectedMenus() {
			this.analyticsStore.activeMenus = this.$refs["menu-filter"].activeMenus.map(menu => menu.menu_id);
			this.togglePopup(false);
			this.showNotification('generic-success');
		},
		scheduleNextRefresh() {
			// Clear any existing timeout
			if(this.realtimeTimeout) {
				clearTimeout(this.realtimeTimeout);
			}
			
			// Schedule the next refresh in 20 seconds
			this.realtimeTimeout = setTimeout(() => {
				this.updateTodayProducts(() => {
					// Schedule the next refresh only after fetch completes
					this.scheduleNextRefresh();
				});
			}, 1000 * 20);
		}
	},
	mounted() {
		if (this.analyticsStore.loadedProducts === false) {
			this.analyticsStore.loadedProducts = true;
			this.periodDates.start = this.$refs["filters"].targetStartDate;
			this.periodDates.end = this.$refs["filters"].targetEndDate;
			this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
			this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
			this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
			this.updateAll();
		} else {
			// wait for the animation to finish
			setTimeout(() => {
				if(this.$refs["filters"]) {
					this.periodDates.start = this.$refs["filters"].targetStartDate;
					this.periodDates.end = this.$refs["filters"].targetEndDate;
					this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
					this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
					this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
				}
				this.updateTodayProducts();
			}, 1000);
		}
		this.scheduleNextRefresh();

		if(!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu();
		}

	},
	beforeUnmount() {
		if(this.realtimeTimeout) {
			clearTimeout(this.realtimeTimeout);
		}
	},
	watch: {
		"userStore.currBusinessId": function (newVal, oldVal) {
			if(newVal != null && newVal !== oldVal) {
				this.menuStore.loadMenu();
				this.updateAll();
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.menus-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	overflow-x: auto !important;
	-ms-overflow-style: none;
	scrollbar-width: none;
	border-radius: 13px;
	background-color: var(--color-bg-primary-2);
	box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
	min-height: 51px;
	flex-grow: 1;
}

.products-analytics-container {
	max-width: 1400px;
	margin: 0 auto;
}

.label-container {
	display: flex;
	justify-content: flex-start;
	margin-bottom: 10px;
}
.extra-filters {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
}
</style>